import { useState, useEffect } from "react";
import { Instagram, Linkedin, Facebook } from "lucide-react";
import { Title } from "./bits";
import { T, UNIVERSES, EMAIL } from "./content";

const NIVEAUX = ["PREMIUM", "OR", "DIAMANT"];

export default function Contact({ lang }) {
  const t = T[lang];
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", univers: "", niveau: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    const h = (e) => setForm((f) => ({ ...f, niveau: e.detail || "" }));
    window.addEventListener("club-apply", h);
    return () => window.removeEventListener("club-apply", h);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    const payload = { ...form, lang };

    // Safety net - Netlify Forms (urlencoded to /), fire-and-forget.
    const enc = new URLSearchParams({ "form-name": "contact", ...payload });
    fetch("/", { method: "POST", headers: { "Content-Type": "application/x-www-form-urlencoded" }, body: enc.toString() }).catch(() => {});

    try {
      const r = await fetch("/.netlify/functions/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error("bad");
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section className="section" id="contact" data-testid="section-contact">
      <div className="container">
        <div className="section-head reveal" style={{ maxWidth: "18ch" }}>
          <Title parts={t.contactTitle} tag="h2" />
        </div>

        <div className="contact__block reveal">
          {status === "success" ? (
            <div className="contact__form" data-testid="contact-success" style={{ display: "flex", alignItems: "center", minHeight: 220 }}>
              <p className="lead" style={{ margin: 0 }}>{t.successMsg}</p>
            </div>
          ) : (
            <form
              className="contact__form"
              name="contact"
              method="POST"
              data-netlify="true"
              netlify-honeypot="bot-field"
              onSubmit={submit}
              data-testid="contact-form"
            >
              <input type="hidden" name="form-name" value="contact" />
              <p hidden><input name="bot-field" onChange={() => {}} /></p>

              <div className="field">
                <input id="c-name" value={form.name} onChange={set("name")} placeholder=" " required data-testid="field-name" />
                <label htmlFor="c-name">{t.formName}</label>
              </div>
              <div className="field">
                <input id="c-email" type="email" value={form.email} onChange={set("email")} placeholder=" " required data-testid="field-email" />
                <label htmlFor="c-email">{t.formEmail}</label>
              </div>
              <div className="field">
                <input id="c-phone" value={form.phone} onChange={set("phone")} placeholder=" " data-testid="field-phone" />
                <label htmlFor="c-phone">{t.formPhone}</label>
              </div>
              <div className="field">
                <input id="c-company" value={form.company} onChange={set("company")} placeholder=" " data-testid="field-company" />
                <label htmlFor="c-company">{t.formCompany}</label>
              </div>
              <div className="field field--select">
                <select id="c-uni" value={form.univers} onChange={set("univers")} data-testid="field-universe">
                  <option value="">{t.formSelect}</option>
                  {UNIVERSES.map((u) => (
                    <option key={u.num} value={lang === "fr" ? u.nameFr : u.nameEn}>{lang === "fr" ? u.nameFr : u.nameEn}</option>
                  ))}
                </select>
                <label htmlFor="c-uni">{t.formUniverse}</label>
              </div>
              <div className="field field--select">
                <select id="c-niveau" value={form.niveau} onChange={set("niveau")} data-testid="field-niveau">
                  <option value="">{t.formSelectNiveau}</option>
                  {NIVEAUX.map((nv) => (
                    <option key={nv} value={nv}>{nv}</option>
                  ))}
                </select>
                <label htmlFor="c-niveau">{t.formNiveau}</label>
              </div>
              <div className="field">
                <textarea id="c-msg" rows={3} value={form.message} onChange={set("message")} placeholder=" " required data-testid="field-message" />
                <label htmlFor="c-msg">{t.formMessage}</label>
              </div>

              <button type="submit" className="btn btn--solid" disabled={status === "sending"} data-testid="contact-submit">
                <span>{status === "sending" ? t.sending : t.formSend}</span>
              </button>
              {status === "error" && (
                <p className="contact__error" data-testid="contact-error" style={{ marginTop: "1rem", fontSize: "0.85rem", color: "var(--fg-2)" }}>{t.errorMsg}</p>
              )}
            </form>
          )}

          <aside className="contact__aside">
            <h4>{t.contactHqLabel}</h4>
            <div className="val">{t.contactHq}</div>
            <h4>{t.contactEmailLabel}</h4>
            <a className="val" href={`mailto:${EMAIL}`} data-testid="contact-email">{EMAIL}</a>
            <h4>{t.contactUniversLabel}</h4>
            <ul className="uni-list">
              {UNIVERSES.map((u) => (
                <li key={u.num}>{lang === "fr" ? u.nameFr : u.nameEn}</li>
              ))}
            </ul>
            <div className="socials">
              <a className="social" href="#" aria-label="Instagram" data-testid="social-instagram"><Instagram size={18} /></a>
              <a className="social" href="#" aria-label="LinkedIn" data-testid="social-linkedin"><Linkedin size={18} /></a>
              <a className="social" href="#" aria-label="Facebook" data-testid="social-facebook"><Facebook size={18} /></a>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
